import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router5';

import { Button, Header, Placeholder, ScrollView } from '$uikit';
import { useTranslator } from '$hooks';
import { Loader, SubscriptionIcon } from '$shared/components';
import { IcHot28, IcSuccess72, IcWallet28 } from '$assets';
import { AuthPopoverState, SubscriptionType } from '$store/models';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { authSelector } from '$store/auth';
import { profileActions } from '$store/profile';
import { FEED, MINE } from '$shared/constants/pages';
import { mainActions, mainSelector } from '$store/main';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';

import { setSubscriptionContentPriority } from '$utils/subscriptionAuthor';
import { ContentPriorityType } from '$store/main/interface';
import router from '$navigation/router';
import { batchActions } from '$store';
import { goBack } from '$navigation/helper';

export const BasicSubscription: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();

  const { isLogged } = useSelector(authSelector);
  const { decryptKeys } = useSelector(mainSelector);
  const hasSubscription = decryptKeys[-SubscriptionType.Basic];

  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [price, setPrice] = useState(0);

  const loadData = useCallback(() => {
    setLoadingStatus('loading');
    setSubscriptionContentPriority(ContentPriorityType.ADULT);
    Api.get(`/balance/subscription/basic`)
      .then((data: any) => {
        setPrice(data.price);
        setLoadingStatus('loaded');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const handleSubscription = useCallback(() => {
    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
    }

    dispatch(toastActions.setToastLoading());
    Api.post<any, any>('/balance/subscription/basic/buy')
      .then((resp) => {
        dispatch(
          batchActions(
            mainActions.setDecryptKey({
              id: -SubscriptionType.Basic,
              decryptKey: resp.decryptKey,
            }),
            toastActions.setToastSuccess(),
          ),
        );
        goBack();
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        if (e.code === 'insufficient_funds') {
          router.navigate(MINE);
        }
      });
  }, [price, isLogged]);

  const handleMine = () => {
    router.navigate(MINE);
  };

  if (hasSubscription) {
    return (
      <Placeholder
        centered
        title={t('subscription_success_title')}
        icon={<IcSuccess72 />}
        actions={
          <Link routeName={FEED}>
            <Button>{t('subscription_success_button_go_to_feed')}</Button>
          </Link>
        }
      >
        {t('subscription_success_basic_caption')}
      </Placeholder>
    );
  }

  return (
    <div className="MediaSubscription">
      <Header showBackButton />
      <ScrollView
        footer={
          <Button fillColor="pink" onClick={handleSubscription}>
            {t('subscription_basic_btn', {
              price,
            })}
          </Button>
        }
      >
        {loadingStatus === 'loading' && <Loader isWrapped />}
        {loadingStatus === 'failed' && (
          <Placeholder
            title={t('failed_title')}
            actions={
              <Button size="small" onClick={loadData}>
                {t('failed_button')}
              </Button>
            }
          >
            {t('failed_caption')}
          </Placeholder>
        )}
        {loadingStatus === 'loaded' && (
          <>
            <SubscriptionIcon type={SubscriptionType.Basic} authorId={0} size="large" />
            <div className="MediaSubscription__content">
              <h1 className="MediaSubscription__title">Ton.Place Basic</h1>
              <p className="MediaSubscription__description">
                {t('subscription_basic_info')}
              </p>
              <div className="MediaSubscription__list">
                <div className="MediaSubscription__item">
                  <div className="MediaSubscription__item__icon _red">
                    <IcHot28 />
                  </div>
                  <div className="MediaSubscription__item__title">
                    {t('subscription_basic_posts_title')}
                  </div>
                  <div className="MediaSubscription__item__description">
                    {t('subscription_basic_posts_caption')}
                  </div>
                </div>
                <div className="MediaSubscription__item" onClick={handleMine}>
                  <div className="MediaSubscription__item__icon _monetization">
                    <IcWallet28 />
                  </div>
                  <div className="MediaSubscription__item__title">
                    {t('subscription_basic_free_title')}
                  </div>
                  <div className="MediaSubscription__item__description">
                    {t('subscription_basic_free_caption')}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Button mode="light" size="extra-small">
                      {t('subscription_basic_free_start')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </ScrollView>
      <AuthPopoverSimple />
    </div>
  );
};
